import { useAuth } from '../context/AuthContext';
import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import {
  Users as UsersIcon, Plus, Search, Download, X, Save, Loader2,
  Shield, UserCheck, UserX, Mail, Building2, Pencil, Trash2,
  Lock, Eye, EyeOff, RefreshCw, Copy, Check, KeyRound, Info, Layers, CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Generate a human-friendly but strong random password (e.g. K9xm-Qr7t-Vn4p)
const generatePassword = () => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const pick = (n) => Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${pick(4)}-${pick(4)}-${pick(4)}`;
};

// Stage 73.20ek — Fully DYNAMIC role sourcing. The role list, labels, and
// chip colours all come from the backend `/api/permissions/assignable-roles`
// endpoint. NO role names are hard-coded here — that was the previous cause
// of drift (deleted HR Admin still appearing, custom roles missing, etc.).

// Exclusive (mutually-exclusive) roles — cannot be combined with anything else.
// These are structural (a Tenant Admin who is also an Employee makes no
// hierarchical sense) rather than tenant-configurable, so it's safe to keep
// this set client-side. Everything else about the role list is dynamic.
const EXCLUSIVE_ROLES = new Set(['super_admin', 'tenant_admin']);

// Fallback chip style for a role key we haven't seen yet (e.g. a legacy user
// still on a deleted role). We highlight it in slate + italic so admins can
// quickly spot and re-assign them.
const UNKNOWN_ROLE_STYLE = 'bg-slate-200 text-slate-500 border-slate-300 italic';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [allCustomRoles, setAllCustomRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '', name: '', role: 'employee', roles: ['employee'], company_id: '', password: generatePassword(),
  });
  const [showPwd, setShowPwd] = useState(false);
  const [pwdCopied, setPwdCopied] = useState(false);
  const [error, setError] = useState('');
  const { user: currentUser } = useAuth();
  const allowedRoles = assignableRoles; // alias — historical name used below

  // Credential confirmation after creation
  const [createdCredential, setCreatedCredential] = useState(null); // { email, password, name }
  const [credEmailCopied, setCredEmailCopied] = useState(false);
  const [credPwdCopied, setCredPwdCopied] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'employee', roles: ['employee'], company_id: '', is_active: true });
  const [editRoleSearch, setEditRoleSearch] = useState('');
  const [editError, setEditError] = useState('');
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);

  useEffect(() => { fetchUsers(); fetchCompanies(); fetchAssignableRoles(); }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) { console.error('Failed to fetch users:', err); }
    finally { setLoading(false); }
  };

  const fetchCompanies = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
    } catch (err) { console.error('Failed to fetch companies:', err); }
  };

  // Stage 73.20ek — Load the tenant-scoped assignable role list. The
  // response already respects role-creation hierarchy, deleted built-ins,
  // and tenant scope, so the UI can render it verbatim.
  const fetchAssignableRoles = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/permissions/assignable-roles`, { withCredentials: true });
      const rolesList = Array.isArray(data?.roles) ? data.roles : [];
      setAssignableRoles(rolesList);
      setAllCustomRoles(rolesList.filter(r => r.is_custom));
    } catch (err) {
      // 403 for lower-tier users → they don't see the Add User modal anyway.
      setAssignableRoles([]);
      setAllCustomRoles([]);
    }
  };

  const openAddModal = () => {
    const defaultRole = allowedRoles.find(r => r.value === 'employee')?.value || allowedRoles[0]?.value || 'employee';
    setFormData({ email: '', name: '', role: defaultRole, roles: [defaultRole], company_id: '', password: generatePassword() });
    setShowPwd(false);
    setPwdCopied(false);
    setError('');
    setShowModal(true);
  };

  // Toggle a role checkbox in the add-user form.
  // Stage 73.20ef fixes:
  //  1. The newly-ticked role becomes the PRIMARY role (was always leaving
  //     'employee' as primary because it stayed at arr[0]).
  //  2. When the admin ticks any non-`employee` role while `employee` is the
  //     ONLY currently-selected (default) role, auto-remove `employee` so the
  //     user isn't unintentionally created with a dual role. If they genuinely
  //     want both, they can re-tick `employee` afterwards.
  //  3. Mutually-exclusive roles (tenant_admin, super_admin) replace the
  //     entire selection.
  const toggleRole = (roleValue) => {
    setError('');
    if (EXCLUSIVE_ROLES.has(roleValue)) {
      setFormData(prev => ({ ...prev, role: roleValue, roles: [roleValue] }));
      return;
    }
    setFormData(prev => {
      const cur = new Set(prev.roles.filter(r => !EXCLUSIVE_ROLES.has(r)));
      const wasChecked = cur.has(roleValue);
      if (wasChecked) {
        cur.delete(roleValue);
      } else {
        // If only the default `employee` was previously selected and the admin
        // is now ticking something else, drop `employee` so we don't create a
        // silent dual-role user (this was the source of the original complaint).
        if (roleValue !== 'employee' && cur.size === 1 && cur.has('employee')) {
          cur.delete('employee');
        }
        cur.add(roleValue);
      }
      const arr = Array.from(cur);
      if (arr.length === 0) arr.push('employee');
      // Primary = the just-added role (if this was a check),
      // otherwise keep the previous primary if it still exists, else fall back to first.
      const newPrimary = !wasChecked
        ? roleValue
        : (arr.includes(prev.role) ? prev.role : arr[0]);
      return { ...prev, role: newPrimary, roles: arr };
    });
  };

  const copyToClipboard = async (text, setFlag) => {
    try {
      await navigator.clipboard.writeText(text);
      setFlag(true);
      setTimeout(() => setFlag(false), 1800);
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Name and email are required'); return;
    }
    if (!formData.password || formData.password.length < 6) {
      setError('Password must be at least 6 characters'); return;
    }
    setError('');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/users`, formData, { withCredentials: true });
      const credential = { email: formData.email, password: formData.password, name: formData.name };
      setShowModal(false);
      setCreatedCredential(credential);
      setFormData({ email: '', name: '', role: 'employee', company_id: '', password: generatePassword() });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally { setSaving(false); }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      if (currentStatus) {
        await axios.delete(`${API_URL}/users/${userId}`, { withCredentials: true });
        toast.success('User deactivated');
      } else {
        await axios.patch(`${API_URL}/users/${userId}`, { is_active: true }, { withCredentials: true });
        toast.success('User activated');
      }
      fetchUsers();
    } catch (err) { toast.error('Failed to update user status'); }
  };

  const handleResetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.name} (${u.email})? A new password will be generated.`)) return;
    try {
      const { data } = await axios.post(
        `${API_URL}/users/${u.id}/reset-password`,
        {},
        { withCredentials: true }
      );
      setCreatedCredential({ email: data.email, password: data.password, name: data.name });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const toggleEditRole = (roleValue) => {
    setEditError('');
    if (EXCLUSIVE_ROLES.has(roleValue)) {
      setEditForm(prev => ({ ...prev, role: roleValue, roles: [roleValue] }));
      return;
    }
    setEditForm(prev => {
      const cur = new Set(prev.roles.filter(r => !EXCLUSIVE_ROLES.has(r)));
      const wasChecked = cur.has(roleValue);
      if (wasChecked) {
        cur.delete(roleValue);
      } else {
        if (roleValue !== 'employee' && cur.size === 1 && cur.has('employee')) {
          cur.delete('employee');
        }
        cur.add(roleValue);
      }
      const arr = Array.from(cur);
      if (arr.length === 0) arr.push('employee');
      const newPrimary = !wasChecked
        ? roleValue
        : (arr.includes(prev.role) ? prev.role : arr[0]);
      return { ...prev, role: newPrimary, roles: arr };
    });
  };

  const removeEditRole = (roleValue) => {
    if (editForm.roles.length <= 1) {
      toast.error('User must keep at least one assigned role');
      return;
    }
    setEditForm(prev => {
      const arr = prev.roles.filter(r => r !== roleValue);
      const newPrimary = arr.includes(prev.role) ? prev.role : arr[0];
      return { ...prev, role: newPrimary, roles: arr };
    });
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    const existingRoles = Array.isArray(u.roles) && u.roles.length > 0 ? u.roles : [u.role || 'employee'];
    setEditForm({
      name: u.name || '',
      role: u.role || existingRoles[0] || 'employee',
      roles: existingRoles,
      company_id: u.company_id || '',
      is_active: u.is_active !== false,
    });
    setEditRoleSearch('');
    setEditError('');
    setShowEditModal(true);
  };

  const handleEditUser = async () => {
    if (!editingUser) return;
    if (!editForm.name.trim()) { setEditError('Name is required'); return; }
    if (!editForm.roles || editForm.roles.length === 0) { setEditError('At least one role must be assigned'); return; }
    setEditError('');
    setSaving(true);
    try {
      const payload = {
        name: editForm.name,
        role: editForm.role || editForm.roles[0],
        roles: editForm.roles,
        is_active: editForm.is_active,
      };
      if (editForm.company_id) payload.company_id = editForm.company_id;
      await axios.patch(`${API_URL}/users/${editingUser.id}`, payload, { withCredentials: true });
      toast.success('User updated with assigned roles');
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Failed to update user');
    } finally { setSaving(false); }
  };

  const handleHardDeleteUser = async (u) => {
    if (!window.confirm(`Permanently DELETE user "${u.name}" (${u.email})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/users/${u.id}?hard=true`, { withCredentials: true });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch =
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.role?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany =
      companyFilter === 'all' ||
      (companyFilter === 'none' && !u.company_id) ||
      u.company_id === companyFilter;
    return matchesSearch && matchesCompany;
  });

  // Stage 73.20ek — Resolve any role key (built-in or `custom:<id>`) to a
  // display object using the dynamic list. Unknown roles (deleted/orphaned)
  // render with the "(deleted)" style so admins spot them immediately and
  // can reassign. Zero hard-coded role names live here — the API is the
  // single source of truth.
  const getRoleInfo = (role) => {
    if (!role) return { value: '', label: 'Unknown', color: UNKNOWN_ROLE_STYLE, isCustom: false };
    const match = assignableRoles.find(r => r.value === role || r.key === role);
    if (match) return { ...match, isCustom: !!match.is_custom };
    // Fallback: try to resolve via ALL custom roles (assignable list is filtered
    // to the caller's scope, so a legitimate custom role assigned by another
    // Tenant Admin may not appear there). Show the raw name if we can.
    if (typeof role === 'string' && role.startsWith('custom:')) {
      const cr = allCustomRoles.find(c => c.key === role);
      return {
        value: role,
        label: cr ? cr.label : '(deleted custom role)',
        color: cr ? cr.color : UNKNOWN_ROLE_STYLE,
        isCustom: true,
      };
    }
    // Built-in role that was DELETED for this tenant — still assigned to a
    // legacy user. Show it as (deleted) so admins can reassign.
    return {
      value: role,
      label: `${role.replace(/_/g, ' ')} (deleted)`,
      color: UNKNOWN_ROLE_STYLE,
      isCustom: false,
    };
  };

  const activeCount = users.filter(u => u.is_active).length;
  // Stage 73.20ek — Admin badge is anyone whose primary role has an "admin"
  // suffix (tenant_admin / hr_admin / payroll_admin / attendance_admin) OR
  // is super_admin. Uses substring rather than a hard-coded list so newly
  // introduced admin variants automatically count.
  const adminCount = users.filter(u => {
    const r = (u.role || '').toLowerCase();
    return r === 'super_admin' || r === 'tenant_admin' || r.endsWith('_admin');
  }).length;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="users-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <UsersIcon className="inline-block w-8 h-8 mr-2 mb-1" /> User Management
            </h1>
            <p className="mt-1 text-sm text-slate-600">Create accounts, assign roles, and manage access permissions</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" data-testid="users-export-btn" onClick={() => {
              if (filteredUsers.length === 0) { toast.error('No users to export'); return; }
              downloadCSV(filteredUsers.map(u => ({
                Name: u.name, Email: u.email, Role: u.role, Status: u.is_active ? 'Active' : 'Inactive',
                Created: new Date(u.created_at).toLocaleDateString(),
              })), 'users_export.csv');
              toast.success('Users exported');
            }}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button data-testid="add-user-button" onClick={openAddModal}
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              <Plus className="w-4 h-4 mr-2" /> Add User
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Total Users</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono" data-testid="total-users-count">{users.length}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-green-600">Active</p>
            <p className="text-2xl font-semibold text-green-700 mt-1 font-mono">{activeCount}</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-purple-600">Admins</p>
            <p className="text-2xl font-semibold text-purple-700 mt-1 font-mono">{adminCount}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-blue-600">Roles in Use</p>
            <p className="text-2xl font-semibold text-blue-700 mt-1 font-mono">{new Set(users.map(u => u.role)).size}</p>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input type="text" placeholder="Search by name, email, or role..." data-testid="search-users-input"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-10 pl-10 pr-4 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]/20 focus:border-[#0052FF]" />
          </div>
          <select
            data-testid="filter-users-company"
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm min-w-[200px]">
            <option value="all">{`All Companies (${users.length})`}</option>
            {companies.map(c => {
              const count = users.filter(u => u.company_id === c.id).length;
              return <option key={c.id} value={c.id}>{`${c.name} (${count})`}</option>;
            })}
            <option value="none">{`No Company (${users.filter(u => !u.company_id).length})`}</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="users-table">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Email</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Company</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Role</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Created</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-12 text-slate-500">
                  <UsersIcon className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No users found</p>
                </td></tr>
              ) : filteredUsers.map(user => {
                const userRolesList = (user.roles && user.roles.length > 0) ? user.roles : [user.role];
                const companyObj = companies.find(c => c.id === user.company_id);
                const companyLabel = companyObj ? companyObj.name : (user.company_id ? '(unknown)' : '—');
                return (
                  <tr key={user.id} data-testid={`user-row-${user.email}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#0052FF]/10 flex items-center justify-center text-[#0052FF] font-semibold text-xs">
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <span className="text-sm font-medium text-slate-900">{user.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{user.email}</td>
                    <td className="py-3 px-4 text-sm" data-testid={`user-company-${user.email}`}>
                      {companyObj ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                          <Building2 className="w-3 h-3" /> {companyObj.name}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">{companyLabel}</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1" data-testid={`user-roles-${user.email}`}>
                        {userRolesList.map(rv => {
                          const info = getRoleInfo(rv);
                          return (
                            <span key={rv} className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${info.color}`}>
                              <Shield className="w-3 h-3 mr-1" /> {info.label}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {user.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
                          <UserCheck className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-100 text-red-800 border border-red-300">
                          <UserX className="w-3 h-3" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{new Date(user.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => openEditModal(user)} data-testid={`edit-user-${user.email}`}
                          className="text-xs px-2 py-1 rounded-md font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 flex items-center gap-1">
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button onClick={() => handleResetPassword(user)} data-testid={`reset-password-${user.email}`}
                          title="Generate a new password and display it"
                          className="text-xs px-2 py-1 rounded-md font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200 flex items-center gap-1">
                          <KeyRound className="w-3 h-3" /> Reset
                        </button>
                        <button onClick={() => toggleUserStatus(user.id, user.is_active)} data-testid={`toggle-user-status-${user.email}`}
                          className={`text-xs px-2 py-1 rounded-md font-medium ${
                            user.is_active
                              ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                              : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                          }`}>
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => handleHardDeleteUser(user)} data-testid={`delete-user-${user.email}`}
                          className="text-xs px-2 py-1 rounded-md font-medium bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ======= ADD USER MODAL ======= */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="add-user-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-slate-50 rounded-t-xl">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-[#0052FF] flex items-center justify-center">
                  <Plus className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Create New User</h2>
                  <p className="text-xs text-slate-500">Set up account with role and company access</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-200 transition-colors" data-testid="close-user-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm font-medium" data-testid="user-form-error">
                  {error}
                </div>
              )}

              {/* Full Name */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name *</label>
                <div className="relative">
                  <UsersIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input data-testid="user-name-input" value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. John Doe"
                    className="w-full h-11 pl-10 pr-4 bg-white border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/10 transition-colors" />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input data-testid="user-email-input" type="email" value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    className="w-full h-11 pl-10 pr-4 bg-white border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/10 transition-colors" />
                </div>
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Company</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                  <select data-testid="user-company-select" value={formData.company_id}
                    onChange={e => setFormData({ ...formData, company_id: e.target.value })}
                    className="w-full h-11 pl-10 pr-3 bg-white border-2 border-slate-200 rounded-lg text-sm appearance-none focus:outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/10 transition-colors">
                    <option value="">Select company</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Role(s) — multi-select checkboxes */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Roles * <span className="text-xs font-normal text-slate-500">(tick one or more — Tenant Admin cannot be combined)</span>
                </label>
                <div className="grid grid-cols-2 gap-2" data-testid="user-roles-checkboxes">
                  {allowedRoles.length === 0 && (
                    <div className="col-span-2 text-xs text-slate-500 italic p-2">
                      You do not have permission to create users.
                    </div>
                  )}
                  {allowedRoles.map(r => {
                    const checked = formData.roles.includes(r.value);
                    const disabled =
                      // If an exclusive role (Tenant Admin) is currently selected, all others are disabled
                      (formData.roles.some(x => EXCLUSIVE_ROLES.has(x)) && !checked)
                      // If a non-exclusive is selected, disable exclusive roles
                      || (EXCLUSIVE_ROLES.has(r.value) && formData.roles.some(x => !EXCLUSIVE_ROLES.has(x)));
                    return (
                      <label
                        key={r.value}
                        data-testid={`role-checkbox-${r.value}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm cursor-pointer transition-colors ${
                          checked ? `${r.color} border-current font-medium` : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                        } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded accent-[#0052FF]"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => !disabled && toggleRole(r.value)}
                        />
                        <Shield className="w-3.5 h-3.5" />
                        {r.label}
                      </label>
                    );
                  })}
                </div>
                {/* Stage 73.20ef — Show which role will be the PRIMARY (the one
                    that unlocks the sidebar + drives `/permissions/me`) so
                    admins understand what a mixed selection means. */}
                {formData.roles.length > 0 && (
                  <div className="mt-2 text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5" data-testid="primary-role-hint">
                    Primary role: <b className="text-blue-900">{getRoleInfo(formData.role).label}</b>
                    {formData.roles.length > 1 && (
                      <> · extras: {formData.roles.filter(r => r !== formData.role).map(r => getRoleInfo(r).label).join(', ')}</>
                    )}
                  </div>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="user-password-input"
                    type={showPwd ? 'text' : 'password'}
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Auto-generated or type your own"
                    className="w-full h-11 pl-10 pr-28 bg-white border-2 border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/10 transition-colors"
                  />
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => setShowPwd(v => !v)}
                      data-testid="toggle-password-visibility"
                      title={showPwd ? 'Hide' : 'Show'}
                      className="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-md"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setFormData({ ...formData, password: generatePassword() }); setPwdCopied(false); }}
                      data-testid="regenerate-password"
                      title="Regenerate"
                      className="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-md"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(formData.password, setPwdCopied)}
                      data-testid="copy-password"
                      title="Copy"
                      className="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-md"
                    >
                      {pwdCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                  <KeyRound className="w-3 h-3" />
                  User will be forced to change this password on first login.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <Button variant="outline" onClick={() => setShowModal(false)} className="px-5">Cancel</Button>
              <Button data-testid="user-submit-button" onClick={handleSubmit} disabled={saving}
                className="bg-[#0052FF] hover:bg-[#0043D1] text-white px-5">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Create User
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ======= EDIT USER MODAL ======= */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="edit-user-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-slate-50 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0052FF] flex items-center justify-center">
                  <Pencil className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Edit User Management & Roles</h2>
                  <p className="text-xs text-slate-500 font-mono">{editingUser.email}</p>
                </div>
              </div>
              <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-200 transition-colors" data-testid="close-edit-user-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              {editError && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm font-medium" data-testid="edit-user-form-error">
                  {editError}
                </div>
              )}

              {/* Basic Details: Name + Company */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name *</label>
                  <div className="relative">
                    <UsersIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input data-testid="edit-user-name-input" value={editForm.name}
                      onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full h-10 pl-10 pr-4 bg-white border-2 border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/10 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Company</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                    <select data-testid="edit-user-company-select" value={editForm.company_id}
                      onChange={e => setEditForm({ ...editForm, company_id: e.target.value })}
                      className="w-full h-10 pl-10 pr-3 bg-white border-2 border-slate-200 rounded-lg text-sm appearance-none focus:outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/10 transition-colors">
                      <option value="">No company assigned</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Current Role Assignments (Chips + Actions) */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                      <Shield className="w-4 h-4 text-[#0052FF]" /> Current Assigned Roles ({editForm.roles.length})
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">Assigned roles grant permissions across modules in this tenant</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPermissionsModal(true)}
                    data-testid="view-effective-permissions-btn"
                    className="text-xs px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-md font-medium flex items-center gap-1.5 shadow-sm transition-colors"
                  >
                    <Layers className="w-3.5 h-3.5 text-[#0052FF]" /> View Permissions
                  </button>
                </div>

                {/* Selected Role Chips */}
                <div className="flex flex-wrap gap-2 pt-1" data-testid="edit-user-role-chips">
                  {editForm.roles.map((rv, idx) => {
                    const info = getRoleInfo(rv);
                    const isPrimary = rv === editForm.role || (idx === 0 && !editForm.roles.includes(editForm.role));
                    return (
                      <div
                        key={rv}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border shadow-sm transition-all ${info.color}`}
                        data-testid={`selected-role-chip-${rv}`}
                      >
                        <Shield className="w-3.5 h-3.5" />
                        <span>{info.label}</span>
                        {isPrimary && (
                          <span className="text-[10px] bg-white/80 text-slate-800 px-1.5 py-0.5 rounded font-mono font-bold uppercase ml-0.5">
                            Primary
                          </span>
                        )}
                        {editForm.roles.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEditRole(rv)}
                            title={`Remove ${info.label}`}
                            data-testid={`remove-role-${rv}`}
                            className="ml-1 text-slate-500 hover:text-slate-900 hover:bg-black/10 rounded p-0.5 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Multi-Role Selection & Search */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Assign Roles <span className="text-xs font-normal text-slate-500">(Select one or more existing roles)</span>
                  </label>
                  <div className="relative w-56">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search roles..."
                      value={editRoleSearch}
                      onChange={e => setEditRoleSearch(e.target.value)}
                      data-testid="search-assignable-roles-input"
                      className="w-full h-8 pl-8 pr-3 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 max-h-60 overflow-y-auto p-1 border border-slate-200 rounded-xl bg-slate-50/50" data-testid="assignable-roles-grid">
                  {allowedRoles
                    .filter(r => {
                      if (!editRoleSearch) return true;
                      const q = editRoleSearch.toLowerCase();
                      return r.label?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q) || r.scope?.toLowerCase().includes(q);
                    })
                    .map(r => {
                      const checked = editForm.roles.includes(r.value);
                      const disabled =
                        (editForm.roles.some(x => EXCLUSIVE_ROLES.has(x)) && !checked)
                        || (EXCLUSIVE_ROLES.has(r.value) && editForm.roles.some(x => !EXCLUSIVE_ROLES.has(x)));
                      return (
                        <div
                          key={r.value}
                          onClick={() => !disabled && toggleEditRole(r.value)}
                          data-testid={`role-option-${r.value}`}
                          className={`p-3 rounded-lg border text-xs cursor-pointer transition-all ${
                            checked
                              ? 'bg-white border-[#0052FF] ring-1 ring-[#0052FF]/20 shadow-sm'
                              : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                          } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 font-medium text-slate-900">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabled}
                                onChange={() => {}} // handled by parent div onClick
                                className="rounded text-[#0052FF] focus:ring-[#0052FF] w-4 h-4"
                              />
                              <span>{r.label}</span>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${r.color}`}>
                              {r.is_custom ? 'Custom' : 'System'}
                            </span>
                          </div>

                          {r.description && (
                            <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{r.description}</p>
                          )}

                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-500">
                            <span>Scope: <b className="text-slate-700">{r.scope || 'Company Scope'}</b></span>
                            {r.permission_count !== undefined && (
                              <>
                                <span>•</span>
                                <span><b className="text-slate-700">{r.permission_count}</b> permissions</span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Status Toggle */}
              <div className="pt-2">
                <label className="flex items-center gap-2 cursor-pointer bg-slate-50 border border-slate-200 p-3 rounded-lg">
                  <input type="checkbox" data-testid="edit-user-active-toggle" checked={editForm.is_active}
                    onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })}
                    className="rounded border-slate-300 text-[#0052FF] w-4 h-4" />
                  <div>
                    <span className="text-sm font-medium text-slate-800">Account Active Status</span>
                    <p className="text-xs text-slate-500">Deactivated users cannot log in to Employee Portal or Admin Dashboard</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between p-5 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <Button
                variant="outline"
                type="button"
                onClick={() => setShowPermissionsModal(true)}
                className="text-xs flex items-center gap-1.5"
              >
                <Layers className="w-3.5 h-3.5 text-[#0052FF]" /> View Effective Permissions
              </Button>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowEditModal(false)} className="px-5">Cancel</Button>
                <Button data-testid="edit-user-submit-button" onClick={handleEditUser} disabled={saving}
                  className="bg-[#0052FF] hover:bg-[#0043D1] text-white px-5">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======= VIEW EFFECTIVE PERMISSIONS MODAL ======= */}
      {showPermissionsModal && editForm && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 overflow-y-auto" data-testid="view-permissions-modal">
          <div className="bg-white rounded-xl w-full max-w-xl shadow-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-slate-50 rounded-t-xl">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Effective Permissions Summary</h2>
                  <p className="text-xs text-slate-500">Combined permissions for {editForm.name || 'User'}</p>
                </div>
              </div>
              <button onClick={() => setShowPermissionsModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-200 transition-colors" data-testid="close-permissions-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wider">Assigned Roles</span>
                  <span className="text-xs font-bold bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded-full font-mono">
                    {editForm.roles.length} Role{editForm.roles.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {editForm.roles.map(rv => {
                    const info = getRoleInfo(rv);
                    return (
                      <span key={rv} className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold border ${info.color}`}>
                        <Shield className="w-3 h-3 mr-1" /> {info.label}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                  Module Access & Actions Breakdown
                </h4>
                <div className="space-y-2">
                  {(() => {
                    const combinedPerms = new Set();
                    const moduleAccessMap = {};

                    editForm.roles.forEach(rk => {
                      const rObj = assignableRoles.find(r => r.value === rk || r.key === rk);
                      if (rObj && Array.isArray(rObj.permissions)) {
                        rObj.permissions.forEach(p => combinedPerms.add(p));
                      }
                    });

                    combinedPerms.forEach(p => {
                      const parts = p.split('.');
                      const mod = parts[0] || 'general';
                      if (!moduleAccessMap[mod]) moduleAccessMap[mod] = [];
                      moduleAccessMap[mod].push(p);
                    });

                    const modKeys = Object.keys(moduleAccessMap).sort();
                    if (modKeys.length === 0) {
                      return <p className="text-xs text-slate-500 italic p-3 text-center">No explicit permissions assigned to selected roles.</p>;
                    }

                    return modKeys.map(modKey => {
                      const list = moduleAccessMap[modKey];
                      return (
                        <div key={modKey} className="border border-slate-200 rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                              {modKey.replace(/_/g, ' ')}
                            </span>
                            <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                              {list.length} action{list.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {list.map(perm => (
                              <span key={perm} className="inline-flex items-center gap-1 text-[11px] font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                                <CheckCircle2 className="w-3 h-3 text-emerald-600" /> {perm}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>

            <div className="flex justify-end p-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <Button onClick={() => setShowPermissionsModal(false)} className="bg-slate-900 text-white px-5">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* ======= CREDENTIAL CONFIRMATION MODAL (after user creation) ======= */}
      {createdCredential && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="credential-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b border-slate-200 bg-gradient-to-br from-green-50 to-emerald-50 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center shrink-0">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">User created successfully</h2>
                  <p className="text-xs text-slate-600 mt-0.5">Share these credentials with <span className="font-medium">{createdCredential.name}</span>. They will be forced to change the password on first login.</p>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Email</label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 border-2 border-slate-200 rounded-lg">
                  <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                  <span data-testid="credential-email" className="flex-1 text-sm font-mono text-slate-900 truncate">{createdCredential.email}</span>
                  <button
                    onClick={() => copyToClipboard(createdCredential.email, setCredEmailCopied)}
                    data-testid="copy-credential-email"
                    className="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-white hover:text-slate-900 rounded-md"
                    title="Copy email"
                  >
                    {credEmailCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Password</label>
                <div className="flex items-center gap-2 p-3 bg-amber-50 border-2 border-amber-200 rounded-lg">
                  <Lock className="w-4 h-4 text-amber-600 shrink-0" />
                  <span data-testid="credential-password" className="flex-1 text-sm font-mono font-semibold text-amber-900 truncate">{createdCredential.password}</span>
                  <button
                    onClick={() => copyToClipboard(createdCredential.password, setCredPwdCopied)}
                    data-testid="copy-credential-password"
                    className="w-8 h-8 flex items-center justify-center text-amber-700 hover:bg-white rounded-md"
                    title="Copy password"
                  >
                    {credPwdCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-500 flex items-start gap-1.5">
                <KeyRound className="w-3 h-3 mt-0.5 shrink-0" />
                This password will not be shown again. Copy and share it securely (e.g. company email / chat).
              </p>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <Button
                onClick={async () => {
                  await copyToClipboard(
                    `Email: ${createdCredential.email}\nPassword: ${createdCredential.password}`,
                    () => toast.success('Credentials copied to clipboard')
                  );
                }}
                variant="outline"
                data-testid="copy-both-credentials"
                className="px-4"
              >
                <Copy className="w-4 h-4 mr-2" /> Copy Both
              </Button>
              <Button
                onClick={() => setCreatedCredential(null)}
                data-testid="close-credential-modal"
                className="bg-[#0052FF] hover:bg-[#0043D1] text-white px-5"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Users;
